import axios from 'axios';
import { setup } from 'axios-cache-adapter';
import { getURLCached } from './cache';
import { hasRecentActivity, IDLE_LIMIT_MS } from 'util/userActivity';

const THREE_MINUTES = 180000;
const FORBIDDEN_HTTP_STATUS_CODE = 403;

export function forceLogout() {
  localStorage.removeItem('user_data');
  if (!localStorage.getItem('alertDisplayed')) {
    const myEvent = new CustomEvent('invalid_token', {
      bubbles: true,
      cancelable: true,
    });
    document.querySelector('body').dispatchEvent(myEvent);
    localStorage.setItem('alertDisplayed', true);
  }
}

const instance = axios.create({
  baseURL: process.env.REACT_APP_API_GATEWAY,
});

const refreshTokenApi = async () => {
  const user = JSON.parse(localStorage.getItem('user_data'));
  instance.defaults.headers.common['Authorization'] = `Bearer ${user.token}`;
  const res = await instance.post('user/auth/refresh');
  return res.data.data.token;
};

let refreshTokenPromise = null;

function attachInterceptors(apiInstance) {
  apiInstance.interceptors.request.use((config) => {
    const userData = JSON.parse(localStorage.getItem('user_data'));
    if (userData?.token) {
      config.headers.Authorization = `Bearer ${userData.token}`;
    }
    return config;
  });

  apiInstance.interceptors.response.use(
    (response) => {
      if (response?.headers?.['content-disposition']) {
        const filename = response.headers['content-disposition']
          ?.split('filename=')[1]
          ?.replaceAll('"', '')
          ?.split('.')[0];
        return { ...response, filename };
      }
      return response;
    },
    async (err) => {
      const user = JSON.parse(localStorage.getItem('user_data'));

      if (err.response?.status === FORBIDDEN_HTTP_STATUS_CODE && user?.token && err.config) {
        // A background poll (dashboard widgets, monitors) hitting an expired token must not
        // silently keep the session alive on its own. Only refresh if there was real user
        // interaction within the idle limit — otherwise let the session actually expire (see CF-527).
        if (!hasRecentActivity(IDLE_LIMIT_MS)) {
          forceLogout();
          return Promise.reject(err);
        }

        if (!refreshTokenPromise) {
          refreshTokenPromise = refreshTokenApi()
            .then((newToken) => {
              const userData = JSON.parse(localStorage.getItem('user_data'));
              userData.token = newToken;
              localStorage.setItem('user_data', JSON.stringify(userData));
              refreshTokenPromise = null;
              return newToken;
            })
            .catch((err) => {
              forceLogout();
              return Promise.reject(err);
            });
        }
        return refreshTokenPromise.then((token) => {
          err.config.headers['Authorization'] = `Bearer ${token}`;
          return axios.request(err.config);
        });
      }
      return Promise.reject(err);
    }
  );
}

function createApiInstance(baseURL) {
  const api = setup({
    baseURL,
    cache: {
      exclude: {
        filter: (req) => getURLCached(req),
        query: false,
      },
      maxAge: THREE_MINUTES,
      methods: ['get'],
    },
  });
  attachInterceptors(api);
  return api;
}

const API_CONFIGS = {
  apiBank: process.env.REACT_APP_API_BANK,
  apiExchange: process.env.REACT_APP_API_EXCHANGE,
  apiMerchant: process.env.REACT_APP_API_MERCHANT,
  apiMisc: process.env.REACT_APP_API_MISC,
  apiNotify: process.env.REACT_APP_API_NOTIFY,
  apiReport: process.env.REACT_APP_API_REPORT,
  apiUser: process.env.REACT_APP_API_USER,
};

const exportedApis = {};
for (const [key, url] of Object.entries(API_CONFIGS)) {
  exportedApis[key] = createApiInstance(url);
}

export const { apiBank, apiExchange, apiMerchant, apiMisc, apiNotify, apiReport, apiUser } = exportedApis;

export { instance, refreshTokenApi };
